/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Graph Execution Scheduler
   Asynchronous-native topological scheduling, dirty propagation,
   concurrency, resource tracking, and cancellation.
   ═══════════════════════════════════════════════════════════════ */

import type { DomainGraphState, NodeId } from '../domain/types';
import type { RenderBackend, TextureHandle } from '../rendering/types';
import type { RendererCapabilities } from '../rendering/capabilities';
import type { ExecutionContext, ExecutionResult, NodeOutputValues, NodeProcessor } from './types';
import { ExecutionCache, DisposableResource } from './cache';

export interface ProcessorResolver {
  getProcessor(nodeType: string): NodeProcessor | undefined;
}

export class ExecutionScheduler {
  private cache = new ExecutionCache();
  private currentAbortController: AbortController | null = null;
  private nodeListeners = new Set<(nodeId: NodeId, outputs: NodeOutputValues) => void>();

  constructor(
    private backend: RenderBackend,
    private capabilities: RendererCapabilities,
    private resolver: ProcessorResolver
  ) {}

  onNodeExecuted(listener: (nodeId: NodeId, outputs: NodeOutputValues) => void): () => void {
    this.nodeListeners.add(listener);
    return () => this.nodeListeners.delete(listener);
  }

  getBackend(): RenderBackend {
    return this.backend;
  }

  renderToCanvas(texture: TextureHandle, targetCanvas: HTMLCanvasElement, maxDimension?: number): void {
    if (this.backend && typeof this.backend.renderToCanvas === 'function') {
      this.backend.renderToCanvas(texture, targetCanvas, maxDimension);
    }
  }

  reset(): void {
    this.cancel();
    this.cache.clear();
  }

  cancel(): void {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  }

  getOutput(nodeId: NodeId): NodeOutputValues | undefined {
    return this.cache.get(nodeId)?.outputs;
  }

  getAllOutputs(): Record<NodeId, NodeOutputValues> {
    const res: Record<NodeId, NodeOutputValues> = {};
    for (const [id, cached] of (this.cache as unknown as { cache: Map<NodeId, { outputs: NodeOutputValues }> }).cache.entries()) {
      res[id] = cached.outputs;
    }
    return res;
  }

  async executeGraph(
    state: DomainGraphState,
    dirtyNodeIds?: Set<NodeId>
  ): Promise<ExecutionResult> {
    // 1. Cancel previous in-flight execution if running
    this.cancel();
    const abortController = new AbortController();
    this.currentAbortController = abortController;
    const signal = abortController.signal;

    // Clean up cache entries for nodes no longer in the graph
    const liveNodeIds = new Set(Object.keys(state.canonical.nodes));
    for (const cachedNodeId of Array.from(this.cache.keys())) {
      if (!liveNodeIds.has(cachedNodeId)) {
        this.cache.invalidate(cachedNodeId);
      }
    }

    const startTime = performance.now();
    const outputs: Record<NodeId, NodeOutputValues> = {};
    const errors: Record<NodeId, string> = {};

    const topologicalOrder = state.indexes.topologicalOrder;
    if (!topologicalOrder) {
      return {
        success: false,
        outputs: {},
        errors: { graph: 'Cannot execute graph with cycle.' },
        durationMs: 0,
      };
    }

    // 2. Iterate nodes in topological order
    for (const nodeId of topologicalOrder) {
      if (signal.aborted) {
        break;
      }

      const node = state.canonical.nodes[nodeId];
      if (!node) continue;

      const processor = this.resolver.getProcessor(node.type);
      if (!processor) {
        errors[nodeId] = `No processor registered for node type '${node.type}'.`;
        continue;
      }

      // 3. Assemble inputs from upstream connected edges
      const nodeInputs: Record<string, unknown> = {};

      // Fill default values from port declarations
      for (const [portId, port] of Object.entries(node.inputs)) {
        if (port.defaultValue !== undefined) {
          nodeInputs[portId] = port.defaultValue;
        }
      }

      // Collect connected values from incoming edges
      const incomingEdgeIds = state.indexes.incomingEdges[nodeId] || [];
      for (const edgeId of incomingEdgeIds) {
        const edge = state.canonical.edges[edgeId];
        if (edge) {
          const upstreamOutputs = outputs[edge.sourceNodeId] || this.cache.get(edge.sourceNodeId)?.outputs;
          if (upstreamOutputs && upstreamOutputs[edge.sourcePortId] !== undefined) {
            nodeInputs[edge.targetPortId] = upstreamOutputs[edge.sourcePortId];
          }
        }
      }

      // 4. Handle bypassed nodes (type-safe pass-through of compatible upstream values)
      if (node.bypassed) {
        const bypassedOutputs: NodeOutputValues = {};
        for (const [outPortId, outPort] of Object.entries(node.outputs)) {
          let matchedVal: unknown;
          // Match by exact ID if available
          if (node.inputs[outPortId] && nodeInputs[outPortId] !== undefined) {
            matchedVal = nodeInputs[outPortId];
          } else {
            // Match by port type
            const sameTypeInputKey = Object.keys(node.inputs).find(
              (inId) => node.inputs[inId]?.type === outPort.type && nodeInputs[inId] !== undefined
            );
            if (sameTypeInputKey) {
              matchedVal = nodeInputs[sameTypeInputKey];
            } else {
              matchedVal = Object.values(nodeInputs)[0] ?? null;
            }
          }
          bypassedOutputs[outPortId] = matchedVal;
        }
        outputs[nodeId] = bypassedOutputs;
        for (const listener of this.nodeListeners) {
          listener(nodeId, bypassedOutputs);
        }
        continue;
      }

      // 5. Check cache if node is not explicitly marked dirty
      const paramHash = JSON.stringify({ params: node.params, bypassed: Boolean(node.bypassed) });
      const inputHash = JSON.stringify(nodeInputs, (_key, val) => {
        if (val && typeof val === 'object' && 'id' in val) {
          return (val as { id: string }).id;
        }
        return val;
      });

      const isDirty = !dirtyNodeIds || dirtyNodeIds.has(nodeId);
      const cached = this.cache.get(nodeId);

      if (!isDirty && cached && cached.paramHash === paramHash && cached.inputHash === inputHash) {
        // Reuse cached output
        outputs[nodeId] = cached.outputs;
        continue;
      }

      // 5. Execute processor
      const allocatedResources: DisposableResource[] = [];
      const context: ExecutionContext = {
        signal,
        capabilities: this.capabilities,
        backend: this.backend,
        allocateResource: <T extends { dispose: () => void }>(res: T): T => {
          allocatedResources.push(res);
          return res;
        },
        warn: (msg: string) => {
          console.warn(`[Node ${nodeId}]`, msg);
        },
      };

      try {
        const resultOrPromise = processor(nodeInputs, node.params, context);
        const nodeOutputs = resultOrPromise instanceof Promise ? await resultOrPromise : resultOrPromise;

        if (signal.aborted) {
          // Dispose resources allocated during this aborted run
          for (const res of allocatedResources) {
            res.dispose();
          }
          break;
        }

        outputs[nodeId] = nodeOutputs;
        this.cache.set(nodeId, nodeOutputs, paramHash, inputHash, allocatedResources);

        // Notify node listeners for live UI preview updates
        for (const listener of this.nodeListeners) {
          listener(nodeId, nodeOutputs);
        }
      } catch (err) {
        // Dispose allocated resources on failure
        for (const res of allocatedResources) {
          res.dispose();
        }

        if (err instanceof DOMException && err.name === 'AbortError') {
          break;
        }

        errors[nodeId] = (err as Error).message || String(err);
      }
    }

    const durationMs = Math.round(performance.now() - startTime);

    if (signal.aborted) {
      return {
        success: false,
        outputs: {},
        errors: { aborted: 'Execution was cancelled.' },
        durationMs,
      };
    }

    return {
      success: Object.keys(errors).length === 0,
      outputs,
      errors,
      durationMs,
    };
  }

  dispose(): void {
    this.cancel();
    this.cache.clear();
    this.nodeListeners.clear();
  }
}
